import { useState, useEffect } from 'react';
import { Settings, Bell, Moon, Sun, Globe, Shield, Volume2, VolumeX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usePreferences } from '@/context/PreferencesContext';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { preferences, loading, updatePreferences } = usePreferences();
  const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(true);
  const [announcementNotificationsEnabled, setAnnouncementNotificationsEnabled] = useState(true);
  const [mentionNotificationsEnabled, setMentionNotificationsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  // Initialize state from preferences
  useEffect(() => {
    if (preferences) {
      setSoundEffectsEnabled(preferences.sound_effects_enabled ?? true);
      setAnnouncementNotificationsEnabled(preferences.announcement_notifications_enabled ?? true);
      setMentionNotificationsEnabled(preferences.mention_notifications_enabled ?? true);
    }
  }, [preferences]);

  const handleSoundEffectsToggle = async (enabled) => {
    setSoundEffectsEnabled(enabled);
    try {
      await updatePreferences({ sound_effects_enabled: enabled });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error updating sound effects setting:', error);
      toast.error('Failed to save settings');
      // Revert on error
      setSoundEffectsEnabled(!enabled);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePreferences({ 
        sound_effects_enabled: soundEffectsEnabled,
        announcement_notifications_enabled: announcementNotificationsEnabled,
        mention_notifications_enabled: mentionNotificationsEnabled,
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-3 sm:p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto w-full space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-4">
            <Settings className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--green-bright))]" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">Settings</h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Customize your learning experience</p>
        </div>

        {/* Notification Settings */}
        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2 text-base sm:text-lg">
              <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
              Notifications
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Manage how you receive notifications</CardDescription>
          </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-3">
              <div>
                  <Label htmlFor="study-reminders" className="text-foreground text-sm">Study Reminders</Label>
                  <p className="text-xs sm:text-sm text-muted-foreground">Get notified about your study schedule</p>
              </div>
              <Switch id="study-reminders" defaultChecked />
            </div>
              <div className="flex items-center justify-between gap-3">
              <div>
                  <Label htmlFor="achievements" className="text-foreground text-sm">Achievement Notifications</Label>
                  <p className="text-xs sm:text-sm text-muted-foreground">Get notified when you unlock achievements</p>
              </div>
              <Switch id="achievements" defaultChecked />
            </div>
              <div className="flex items-center justify-between gap-3">
              <div>
                  <Label htmlFor="announcement-notifications" className="text-foreground text-sm">Announcement Notifications</Label>
                  <p className="text-xs sm:text-sm text-muted-foreground">Get notified about new announcements</p>
              </div>
              <Switch 
                id="announcement-notifications" 
                checked={announcementNotificationsEnabled}
                onCheckedChange={async (enabled) => {
                  setAnnouncementNotificationsEnabled(enabled);
                  try {
                    await updatePreferences({ announcement_notifications_enabled: enabled });
                    toast.success('Settings saved successfully');
                  } catch (error) {
                    console.error('Error updating announcement notifications:', error);
                    toast.error('Failed to save settings');
                    setAnnouncementNotificationsEnabled(!enabled);
                  }
                }}
                disabled={loading || saving}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="mention-notifications" className="text-foreground text-sm">@Mention Notifications</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">Get notified when you are mentioned in posts</p>
              </div>
              <Switch 
                id="mention-notifications" 
                checked={mentionNotificationsEnabled}
                onCheckedChange={async (enabled) => {
                  setMentionNotificationsEnabled(enabled);
                  try {
                    await updatePreferences({ mention_notifications_enabled: enabled });
                    toast.success('Settings saved successfully');
                  } catch (error) {
                    console.error('Error updating mention notifications:', error);
                    toast.error('Failed to save settings');
                    setMentionNotificationsEnabled(!enabled);
                  }
                }}
                disabled={loading || saving}
              />
            </div>
          </CardContent>
        </Card>

        {/* Appearance Settings */}
        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2 text-base sm:text-lg">
              <Moon className="w-5 h-5 sm:w-6 sm:h-6" />
              Appearance
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Customize the look and feel</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="dark-mode" className="text-foreground text-sm">Dark Mode</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">Toggle dark theme</p>
              </div>
              <Switch id="dark-mode" defaultChecked />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="animations" className="text-foreground text-sm">Animations</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">Enable UI animations</p>
              </div>
              <Switch id="animations" defaultChecked />
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2 text-base sm:text-lg">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6" />
              Privacy
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Control your privacy settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="profile-visibility" className="text-foreground text-sm">Profile Visibility</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">Control who can see your profile</p>
              </div>
              <Switch id="profile-visibility" defaultChecked />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="activity-status" className="text-foreground text-sm">Activity Status</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">Show when you're online</p>
              </div>
              <Switch id="activity-status" defaultChecked />
            </div>
          </CardContent>
        </Card>

        {/* Language & Region */}
        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2 text-base sm:text-lg">
              <Globe className="w-5 h-5 sm:w-6 sm:h-6" />
              Language & Region
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Set your preferred language and region</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
                <Label htmlFor="language" className="text-foreground text-sm">Language</Label>
              <select
                id="language"
                  className="w-full mt-2 px-3 py-2 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg text-foreground text-sm"
                defaultValue="en"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Sound Settings */}
        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2 text-base sm:text-lg">
              <Volume2 className="w-5 h-5 sm:w-6 sm:h-6" />
              Sound
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Manage audio settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="sound-effects" className="text-foreground text-sm">Sound Effects</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">Enable sound effects</p>
              </div>
              <Switch 
                id="sound-effects" 
                checked={soundEffectsEnabled}
                onCheckedChange={handleSoundEffectsToggle}
                disabled={loading || saving}
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))]"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

